import { useState, useMemo, useEffect } from 'react';
import { useApproveAccount } from '@/features/card/hooks/useCard/useApproveAccount';
import { Card } from '@/features/card/types/card.types';
import { useAuthContext } from '@/features/auth/context/AuthContext/AuthContext';
import { useToast, useSpinner } from '@chikara/ui';
import { customerService } from '@/features/card/services/customerService';
import { t } from '@/i18n';

interface UseApproveCardFormProps {
    isOpen: boolean;
    onClose: () => void;
    card: Card;
    onSuccess: () => void;
}

export const useApproveCardForm = ({ isOpen, onClose, card, onSuccess }: UseApproveCardFormProps) => {
    const toast = useToast();
    const { show, hide } = useSpinner();
    const { payload: authPayload } = useAuthContext();
    const { approveAccount } = useApproveAccount();

    const [limitInput, setLimitInput] = useState<string>('');
    const [globalAvailable, setGlobalAvailable] = useState<number>(0);
    const [localError, setLocalError] = useState<string | null>(null);

    const limit = useMemo(() => {
        return limitInput ? Number(limitInput.replace(/[$,]/g, '')) : 0;
    }, [limitInput]);

    useEffect(() => {
        const fetchSummary = async () => {
            if (isOpen && authPayload?.customer_uuid) {
                show();
                try {
                    const response = await customerService.getCustomerSummary();
                    setGlobalAvailable(response.credit_line.total_available_to_assign);
                } catch (error) {
                    console.error('Error fetching customer summary:', error);
                } finally {
                    hide();
                }
            }
        };

        if (isOpen) {
            fetchSummary();
            setLimitInput('');
            setLocalError(null);
        }
    }, [isOpen, authPayload?.customer_uuid, show, hide]);

    const availableToAssign = globalAvailable;

    const isLimitExceeded = useMemo(() => {
        return limit > availableToAssign;
    }, [limit, availableToAssign]);

    const handleLimitChange = (value: string) => {
        setLimitInput(value);
        const numericLimit = value ? Number(value.replace(/[$,]/g, '')) : 0;
        if (numericLimit > availableToAssign) {
            setLocalError(t('dashboard.card.detail.modals.approveCard.errors.exceeded'));
        } else {
            setLocalError(null);
        }
    };

    const handleConfirm = async () => {
        if (isLimitExceeded || limit <= 0) return;

        const payload = {
            status: "approved",
            approved_limit: limit,
            currency: "MXN",
            account_uuid: card.uuid,
            user_uuid: authPayload?.user_uuid ? authPayload?.user_uuid : ""
        }

        const success = await approveAccount(payload);
        if (success) {
            toast.success(t('dashboard.card.detail.modals.approveCard.success'));
            onSuccess();
            onClose();
        }
    };

    const isActionDisabled = useMemo(() => {
        return isLimitExceeded || limit <= 0;
    }, [isLimitExceeded, limit]);

    return {
        limitInput,
        limit,
        globalAvailable,
        availableToAssign,
        localError,
        isActionDisabled,
        handleLimitChange,
        handleConfirm,
    };
};
